import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginForm from './components/LoginForm';
import Layout from './components/Layout';

const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ 
  children, 
  adminOnly = false 
}) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        טוען...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <LoginForm />} 
      />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/members" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/debts" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/services" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/events" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/notifications" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/campaigns" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="/campaigns/:id" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  // Unregister any existing service workers and clear old PWA caches
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister().then(() => {
            console.log('Service worker unregistered');
          });
        });
      });
    }
    if (typeof caches !== 'undefined') {
      caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
    }
  }, []);

  // Global CSS to prevent horizontal scroll
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      * {
        box-sizing: border-box;
      }
      html, body {
        overflow-x: hidden;
        max-width: 100vw;
        margin: 0;
        padding: 0;
      }
      #root {
        overflow-x: hidden;
        max-width: 100vw;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
                fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
                direction: 'rtl',
                textAlign: 'right'
              },
              success: {
                iconTheme: {
                  primary: '#4ade80',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
